import { FaFileAlt, FaShoppingCart, FaUsers } from "react-icons/fa";
import {
  getProcurementServicePage,
  getProcurementServiceSlug,
} from "../constants/procurementServicePages";

const ICON_BY_SERVICE_CODE = {
  supplier_discovery_reports: FaFileAlt,
  managed_procurement_services: FaShoppingCart,
  procurement_retainer_services: FaUsers,
};

const formatRupeeAmount = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

export const resolvePublicServicePricing = (service, presentation = null) => {
  const pricing = service?.active_pricing?.[0] || null;
  const fallbackAmount =
    service?.starting_price != null ? formatRupeeAmount(service.starting_price) : null;

  if (pricing) {
    switch (pricing.pricing_type) {
      case "fixed": {
        const amount =
          pricing.base_price != null
            ? formatRupeeAmount(pricing.base_price)
            : fallbackAmount;
        return {
          priceLabel: presentation?.pricingLabel || "Starting from",
          priceAmount: amount || service?.starting_price_label || "Contact us",
          priceSuffix: presentation?.pricingSuffix || null,
        };
      }
      case "percentage_of_procurement_value": {
        const min = pricing.percentage_min;
        const max = pricing.percentage_max;
        let amount = "—";
        if (min != null && max != null && Number(max) !== Number(min)) {
          amount = `${min}%–${max}%`;
        } else if (min != null) {
          amount = `${min}%`;
        } else if (max != null) {
          amount = `${max}%`;
        }
        return {
          priceLabel: presentation?.priceLabel || "Service fee",
          priceAmount: amount,
          priceSuffix: presentation?.pricingSuffix || "of procurement value",
        };
      }
      case "monthly_retainer": {
        const amount =
          pricing.monthly_retainer != null
            ? formatRupeeAmount(pricing.monthly_retainer)
            : fallbackAmount;
        return {
          priceLabel: presentation?.priceLabel || "Starting from",
          priceAmount: amount || service?.starting_price_label || "Contact us",
          priceSuffix: presentation?.pricingSuffix || "per month",
        };
      }
      default:
        break;
    }
  }

  if (service?.starting_price_label) {
    return {
      priceLabel: presentation?.priceLabel || "Starting from",
      priceAmount: service.starting_price_label,
      priceSuffix: presentation?.pricingSuffix || null,
    };
  }

  return {
    priceLabel: presentation?.priceLabel || "Starting from",
    priceAmount: fallbackAmount || "Contact us",
    priceSuffix: presentation?.pricingSuffix || null,
  };
};

/** Merge DB catalogue row with static presentation/SEO fallbacks. */
export const buildPublicServiceViewModel = (apiService) => {
  if (!apiService) return null;

  const slug =
    apiService.slug ||
    getProcurementServiceSlug(apiService.service_code) ||
    String(apiService.service_code || "").replace(/_/g, "-");

  const staticPage = getProcurementServicePage(slug);
  const presentation = staticPage?.package || {};
  const pricing = resolvePublicServicePricing(apiService, presentation);
  const features = Array.isArray(apiService.features)
    ? apiService.features.filter(Boolean)
    : [];

  return {
    id: apiService.id,
    slug,
    serviceCode: apiService.service_code,
    seo: {
      title:
        staticPage?.seo?.title ||
        `${apiService.service_name || "Procurement Service"} | Trocanet Procurement Services`,
      description:
        apiService.service_description?.trim() ||
        staticPage?.seo?.description ||
        "Procurement-as-a-Service from Trocanet.",
      canonicalPath: `/procurement-services/${slug}`,
    },
    hero: {
      eyebrow: apiService.service_name || staticPage?.hero?.eyebrow,
      title: staticPage?.hero?.title || apiService.service_name,
      description:
        apiService.service_description?.trim() ||
        staticPage?.hero?.description ||
        "",
      primaryCta: staticPage?.hero?.primaryCta || "Start Procurement Request",
      secondaryCta: staticPage?.hero?.secondaryCta || "Schedule Consultation",
    },
    package: {
      Icon: ICON_BY_SERVICE_CODE[apiService.service_code] || presentation.Icon || FaShoppingCart,
      visualVariant: presentation.visualVariant || null,
      imageUrl: presentation.imageUrl || null,
      title: apiService.service_name || presentation.title,
      description:
        apiService.service_description?.trim() ||
        presentation.description ||
        "",
      features:
        features.length > 0
          ? features
          : presentation.features || [],
      bestFor: apiService.best_for?.trim() || presentation.bestFor || null,
      pricingLabel: pricing.priceLabel,
      pricingAmount: pricing.priceAmount,
      pricingSuffix: pricing.priceSuffix,
      ctaLabel: presentation.ctaLabel || "Submit Request",
    },
  };
};

export const buildPublicCatalogueViewModels = (apiServices) =>
  (Array.isArray(apiServices) ? apiServices : [])
    .map(buildPublicServiceViewModel)
    .filter(Boolean);

export const extractCatalogueArray = (response) => {
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
};

export const extractDetailPayload = (response) => {
  const data = response?.data ?? response;
  if (!data?.service) return null;
  return buildPublicServiceViewModel({
    ...data.service,
    slug: data.slug,
    active_pricing: data.pricings || data.service.active_pricing,
  });
};
